import {
  Box,
  Button,
  ButtonBase,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import PersonIcon from "@mui/icons-material/Person";
import PeopleIcon from "@mui/icons-material/People";
import API from "../../api/axiosConfig";
import { useUser } from "../../context/UserContext"; // Import your hook
//import { useToast } from "../../context/ToastContext";

export default function ReimbursementManagerPage() {
  /* 
        This page displays all reimbursement objects in the database corresponding to
        the user accessing the page. The user can view the reason, status, and amount
        of each reimbursement. If they want to view the receipt, they will click the
        corresponding reimbursement paper to be redirected to the full reimbursement
        details page.
    */

  //const showToast = useToast();

  const { isAdmin } = useUser();

  // Sets up the navigator
  const navigate = useNavigate();

  // Initialize the loading state
  const [loading, setLoading] = useState(true);

  // Initialize the reimbursement list
  const [rList, setRlist] = useState([]);

  // Mobile checker
  const isMobile = useIsMobile();

  // Admin POV
  const [view, setView] = useState("user");

  // Reimbursement status state
  const [rStatus, setRStatus] = useState("ALL");

  // Format for US currency
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  const userFetch = async () => {
    try {
      const response = await API.post("/api/reimbursements/list", {});

      // If 200 OK status is received, set the reimbursement list accordingly
      setRlist(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setRlist([]);
      } else {
        alert("Error fetching reimbursement list!");
      }
    }
  };

  const allFetch = async () => {
    try {
      const response = await API.post("/api/reimbursements/listall", {});

      // If 200 OK status is received, set the reimbursement list accordingly
      setRlist(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        // If 404 Not found status is received set reimbursement list to empty list
        setRlist([]);
      } else {
        alert("Error fetching reimbursement list!");
      }
    }
  };

  // This function is used to handle the filter change
  const handleFilter = (event) => {
    const newStatus = event.target.value;
    setRStatus(newStatus);
  };

  // This function handles the view that an admin is selecting
  const handleChange = async (event, nextView) => {
    // Prevent deselecting (nextView will be null if clicking the active button)
    if (nextView !== null) {
      setLoading(true);
      setView(nextView); // Set the view immediately so the button highlights

      try {
        if (nextView === "user") {
          await userFetch();
        } else if (nextView === "all") {
          await allFetch();
        }
      } finally {
        setLoading(false);
      }
    }
  };

  // Runs on component mount
  useEffect(() => {
    // The asynchronous function to set the user data
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([userFetch()]);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box marginTop={2}>
        <Typography variant="italic">Loading reimbursements...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          margin: "16px",
          textAlign: "center",
          marginBottom: "32px",
        }}
      >
        Reimbursement Manager
      </Typography>

      <Button
        variant="contained"
        onClick={() => navigate("/reimbursements/add")}
        sx={{ width: isMobile ? "55vw" : "30vw" }}
      >
        New Reimbursement
      </Button>

      {/* Admin Interface */}
      {isAdmin && (
        <Box
          marginTop={3}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            sx={(theme) => ({ color: theme.palette.paper.light, mb: 1 })}
          >
            Manager View
          </Typography>

          <Stack direction="column" alignItems="center" spacing={1}>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={handleChange}
              aria-label="reimbursement view"
              size="medium"
              orientation={isMobile ? "vertical" : "horizontal"}
              sx={(theme) => ({
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${theme.palette.paper.light}`,
                borderRadius: 5,
                "& .MuiToggleButton-root": {
                  // Non-selected text color
                  color: theme.palette.paper.light,
                  borderColor: theme.palette.paper.light,
                  width: isMobile ? "55vw" : "30vw",
                  height: isMobile ? 96 : 64,
                  // The Active/Selected State
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.paper.default,
                    color: theme.palette.paper.verylight,
                    fontWeight: "bold",
                    // Keep the same colors on hover when already selected
                    "&:hover": {
                      backgroundColor: theme.palette.paper.default,
                      opacity: 0.9,
                    },
                  },
                },
              })}
            >
              <ToggleButton
                value="user"
                aria-label="your reimbursements"
                sx={{ borderRadius: 5 }}
              >
                <PersonIcon sx={{ mr: 1 }} />
                Your Requests
              </ToggleButton>

              {isAdmin && (
                <ToggleButton
                  value="all"
                  aria-label="all reimbursements"
                  sx={{ borderRadius: 5 }}
                >
                  <PeopleIcon sx={{ mr: 1 }} />
                  All Requests
                </ToggleButton>
              )}
            </ToggleButtonGroup>

            <Typography
              variant="caption"
              sx={(theme) => ({ color: theme.palette.paper.light })}
            >
              {view === "user"
                ? "Showing your submitted requests"
                : "Showing all employee requests"}
            </Typography>
          </Stack>
        </Box>
      )}

      <Divider
        sx={(theme) => ({
          borderColor: theme.palette.paper.light,
          borderBottomWidth: "1px", // This ensures it is a visible line
          mb: 2,
          mt: 3,
          width: "80vw",
          justifySelf: "center",
        })}
      />
      <Typography variant="h5">Your Reimbursements</Typography>

      <Box
        sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 3 }}
      >
        {/* Status Filter */}
        <FormControl
          size="small"
          sx={{
            // Centering the form control horizontally if not using the Box wrapper
            mx: "auto",
          }}
        >
          <InputLabel
            id="status-filter-label"
            sx={(theme) => ({
              color: theme.palette.paper.light,
              "&.Mui-focused": {
                color: theme.palette.paper.verylight,
              },
            })}
          >
            Status Filter
          </InputLabel>
          <Select
            labelId="status-filter-label"
            id="status-filter-select"
            value={rStatus}
            label="Status Filter"
            onChange={handleFilter}
            sx={(theme) => ({
              color: theme.palette.paper.verylight,
              width: "90vw",
              maxWidth: 600,
              height: isMobile ? 96 : 64,
              // Custom transition for the highlight outline
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.paper.light,
                transition:
                  "border-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.paper.verylight,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.paper.verylight,
                borderWidth: "2px",
              },
              // Ensuring the icon also follows the transition
              "& .MuiSvgIcon-root": {
                color: theme.palette.paper.light,
                transition: "color 0.3s ease-in-out",
              },
              "&.Mui-focused .MuiSvgIcon-root": {
                color: theme.palette.paper.verylight,
              },
            })}
            MenuProps={{
              PaperProps: {
                sx: (theme) => ({
                  bgcolor: theme.palette.paper.dark,
                  color: theme.palette.paper.verylight,
                  "& .MuiMenuItem-root:hover": {
                    bgcolor: theme.palette.paper.dark,
                  },
                }),
              },
            }}
          >
            <MenuItem value="ALL">ALL</MenuItem>
            <MenuItem value="PENDING">PENDING</MenuItem>
            <MenuItem value="APPROVED">APPROVED</MenuItem>
            <MenuItem value="DENIED">DENIED</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Reimbursement List Display */}
      <Box
        sx={{
          mt: 2,
          mb: 4,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          alignItems: "center",
        }}
      >
        {rList.length > 0 ? (
          rList.map(
            (reimbursement) =>
              (reimbursement.status === rStatus || rStatus === "ALL") && (
                <Box key={reimbursement.id}>
                  <ButtonBase
                    onClick={() => {
                      const selection = window.getSelection().toString();
                      // Only navigate if the user DID NOT highlight any text
                      if (!selection || selection.length < 3) {
                        navigate(`/reimbursements/${reimbursement.id}`);
                      }
                    }}
                    sx={{
                      maxWidth: 600,
                      borderRadius: 1, // Matches the default Paper border radius
                      overflow: "hidden",
                    }}
                  >
                    <Paper
                      sx={(theme) => ({
                        width: "90vw",
                        maxWidth: "900px",
                        height: "100px",
                        transition:
                          "outline-color 0.3s ease-in-out, background-color 0.3s ease-in-out",
                        "&:hover": {
                          backgroundColor: theme.palette.paper.dark,
                          cursor: "pointer",
                        },
                        userSelect: "text",
                        "& *": { userSelect: "text" },
                        alignContent: "center",
                        padding: 1,
                      })}
                    >
                      <Stack
                        direction="row"
                        justifyContent={"center"}
                        alignItems={"center"}
                      >
                        <Box width={isMobile ? "15%" : "10%"}>
                          <Typography
                            sx={(theme) => ({
                              color: theme.palette.paper.light,
                            })}
                          >
                            {reimbursement.id}
                          </Typography>
                        </Box>
                        {isMobile ? (
                          <Box width="15%" />
                        ) : (
                          <Box width="40%">
                            <Typography
                              sx={(theme) => ({
                                color: theme.palette.paper.verylight,
                                wordBreak: "normal",
                                overflowWrap: "break-word",
                                display: "block",
                                lineHeight: "1.2",
                              })}
                            >
                              {reimbursement.reason || "No reason provided."}
                            </Typography>
                          </Box>
                        )}
                        <Box
                          width={isMobile ? "60%" : "30%"}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <Typography
                            fontWeight="bold"
                            fontSize={24}
                            sx={(theme) => ({
                              color: theme.palette.paper.verylight,
                            })}
                          >
                            {formatter.format(reimbursement.amount)}
                          </Typography>
                        </Box>
                        <Box width={isMobile ? "30%" : "20%"}>
                          <Typography
                            fontWeight="bold"
                            sx={(theme) => ({
                              color:
                                reimbursement.status === "PENDING"
                                  ? theme.palette.status.middle
                                  : reimbursement.status === "APPROVED"
                                    ? theme.palette.status.good
                                    : theme.palette.status.bad,
                            })}
                          >
                            {reimbursement.status}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </ButtonBase>
                </Box>
              ),
          )
        ) : (
          <Typography variant="italic" sx={{ mt: 4 }}>
            No reimbursements found.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
